/**
 * SitePick front — 実物ビューア（/sites/{slug}/live/）の「全画面で見る」（site-share spec §4.5 / §9.4）。
 *
 * 生のデモ URL を開く「新しいタブで開く」の代わりに、viewer 内で上下のバーを畳んで iframe を画面いっぱいに
 * 広げる（CSS の表示切替。Fullscreen API は使わない = iPhone の Safari でも同じ挙動）。
 *  - iframe の属性・src・preview.js・計測には触れない（view_preview は再発火しない）
 *  - 切替の直後に resize を 1 回送り、preview.js の縮小率（--sp-live-scale）を再計算させる
 *  - 脱出口は浮動ピルの「全画面を閉じる」（デモ内をタップすると Escape は親に届かないため。Escape は補助）
 *
 * あわせて、timeout 後の表示の復帰を行う（kill switch に関係なく動かす）: iframe-frame.js は timeout 後に
 * 不透明な fallback を出したまま、遅れて load されても戻さない。ここで load を別に監視し、fallback が
 * 表示中なら hidden に戻す。計測は送らない（成功と失敗を両方数えない既存方針）。
 */

/**
 * timeout 後に遅れて load されたら fallback を隠す。
 *
 * @param {ParentNode} root
 */
function watchLateLoad(root) {
  const iframe = root.querySelector(".p-live__iframe");
  const fallback = root.querySelector("[data-sp-preview-fallback]");
  if (!(iframe instanceof HTMLIFrameElement) || !fallback) {
    return;
  }
  iframe.addEventListener("load", () => {
    // fallback は昇格（src の設定）後の timeout でしか出ないので、この load は about:blank ではなくデモのもの。
    if (!fallback.hidden && iframe.dataset.spPromoted === "1") {
      fallback.hidden = true;
    }
  });
}

/**
 * 初期化。`/live/` 以外（対象要素が無い）では何もしない。
 *
 * @param {ParentNode} [root]
 * @returns {void}
 */
export function initLiveFullscreen(root = document) {
  watchLateLoad(root);

  const live = root.querySelector(".p-live");
  const openBtn = root.querySelector(".js-sp-live-full");
  if (!live || !(openBtn instanceof HTMLElement)) {
    return;
  }
  const closeBtns = [...root.querySelectorAll(".js-sp-live-full-close")];
  const html = document.documentElement;

  const set = (on) => {
    live.classList.toggle("is-fullscreen", on);
    html.classList.toggle("has-live-fullscreen", on);
    openBtn.setAttribute("aria-pressed", on ? "true" : "false");
    if (!on) {
      // 浮動ピルの中で開いていた共有パネルは閉じる。
      live.querySelectorAll(".p-live__float .js-sp-share[open]").forEach((el) => {
        el.open = false;
      });
    }
    // preview.js の縮小率の再計算を起動する（クラスの付け外しだけでは window の resize が起きない）。
    window.dispatchEvent(new Event("resize"));
  };

  openBtn.hidden = false;
  openBtn.addEventListener("click", () => {
    set(true);
    if (closeBtns[0]) {
      closeBtns[0].focus({ preventScroll: true });
    }
  });
  closeBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      set(false);
      openBtn.focus({ preventScroll: true });
    });
  });
  document.addEventListener("keydown", (event) => {
    // 共有パネルを閉じた Escape（share.js が preventDefault する）では全画面を解除しない。
    if (event.key !== "Escape" || event.defaultPrevented || !live.classList.contains("is-fullscreen")) {
      return;
    }
    set(false);
    openBtn.focus({ preventScroll: true });
  });
}
